import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Annotation, ChannelOptions, Message } from 'ably';
import { ChannelProvider, useAbly, useChannel } from 'ably/react';
import { flushSync } from 'react-dom';
import { useStore } from 'common/store';
import NotificationToastStack from 'components/NotificationCenter/NotificationToastStack';
import { BrowserNotificationsState, useBrowserNotifications } from 'services/hooks/useBrowserNotifications';
import {
  applyAnnotationSummaryFromMessage,
  NOTIFICATION_ANNOTATION_DISTINCT_TYPE,
  publishDistinctAnnotation,
} from './notificationAnnotations';
import {
  broadcastNotificationEvent,
  NotificationBroadcastEvent,
  openNotificationBroadcastChannel,
} from './notificationCenterBroadcast';
import {
  evictExpired,
  loadFlagsForUser,
  migrateFromLocalStorage,
  persistDismissed,
  persistDismissedMany,
  persistRead,
} from './notificationCenterIndexedDB';

/** Last N messages delivered on channel attach (Ably rewind). See https://ably.com/docs/channels/options/rewind */
const NOTIFICATION_CHANNEL_REWIND_MESSAGES = '10';

/** Max entries kept in the in-memory list; oldest entries beyond this are dropped. */
const MAX_NOTIFICATION_LIST_SIZE = 50;

/** Messages older than mount time minus this buffer are treated as rewind/history (skip toasts). */
const REWIND_TOAST_CUTOFF_MS = 3000;

/**
 * Ably app configuration required for this feature (set in the Ably dashboard):
 *
 * 1. **Channel rules** — the `user:*` namespace must have:
 *    - "Persist last message" enabled (or message history) so rewind delivers real history
 *      beyond the in-memory buffer. Without persistence, rewind only covers the short
 *      in-memory window on the Ably edge node.
 *    - "Mutable messages" / annotations enabled so `annotation_publish` and
 *      `annotation_subscribe` modes are accepted. Without this, annotation publishes silently
 *      fail and cross-tab/cross-device read sync via Ably will not work.
 *
 * 2. **Token capabilities** — the JWT issued by the backend already includes
 *    `history`, `subscribe`, `annotation-subscribe`, and `annotation-publish` on user channels.
 *    No backend changes needed for current functionality.
 *
 * 3. **Local fallback** — `notificationCenterIndexedDB.ts` stores read/dismissed flags in
 *    IndexedDB with timestamp-based eviction mirroring Ably retention. Falls back to empty state
 *    in private mode. `notificationCenterBroadcast.ts` syncs across same-origin tabs via
 *    BroadcastChannel. These work independently of Ably persistence.
 */
const NOTIFICATION_CHANNEL_OPTIONS: ChannelOptions = {
  params: { rewind: NOTIFICATION_CHANNEL_REWIND_MESSAGES },
  modes: ['subscribe', 'annotation_publish', 'annotation_subscribe'],
};

const GLOBAL_CHANNEL_NAME = 'global';
const GLOBAL_NOTIF_PREF_KEY = 'scalar_global_notifications_enabled';
const GLOBAL_CHANNEL_OPTIONS: ChannelOptions = {
  params: { rewind: NOTIFICATION_CHANNEL_REWIND_MESSAGES },
  modes: ['subscribe'],
};

export type NotificationStatus = 'in_progress' | 'completed' | 'failed';

export const NOTIFICATION_TYPE = {
  JOB: 'job',
  GLOBAL: 'global',
  GENERAL: 'general',
} as const;

export type NotificationType = typeof NOTIFICATION_TYPE[keyof typeof NOTIFICATION_TYPE];

/**
 * How long after acknowledgment a notification stays visible in the panel.
 * Whichever expires first — this TTL or the Ably channel TTL — wins.
 */
export const ACK_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

export interface JobAppNotification {
  type: typeof NOTIFICATION_TYPE.JOB;
  id: string;
  status: NotificationStatus;
  job: GenerationJobPayload;
  addedAt: Date;
  updatedAt: Date;
  dismissed: boolean;
  acknowledgedAt?: number;
  ablySerial?: string;
  read?: boolean;
  firmSlug?: string;
  companySlug?: string;
}

export interface GlobalAppNotification {
  type: typeof NOTIFICATION_TYPE.GLOBAL;
  id: string;
  title: string;
  content: string;
  link?: string;
  linkLabel?: string;
  eventType: string;
  addedAt: Date;
  updatedAt: Date;
  dismissed: boolean;
  acknowledgedAt?: number;
  ablySerial?: string;
  read?: boolean;
}

export interface GeneralAppNotification {
  type: typeof NOTIFICATION_TYPE.GENERAL;
  id: string;
  title: string;
  content: string;
  link?: string;
  linkLabel?: string;
  addedAt: Date;
  updatedAt: Date;
  dismissed: boolean;
  acknowledgedAt?: number;
  ablySerial?: string;
  read?: boolean;
}

export type AppNotification = JobAppNotification | GlobalAppNotification | GeneralAppNotification;

interface NotificationCenterContextType {
  notifications: AppNotification[];
  /** Not dismissed and not marked read (badge count). Excludes global/general when disabled. */
  unreadCount: number;
  dismissNotification: (id: string) => void;
  dismissAll: () => void;
  markNotificationRead: (id: string) => void;
  markAllRead: () => void;
  browserNotifications: BrowserNotificationsState;
  globalNotifEnabled: boolean;
  toggleGlobalNotifications: () => void;
}

const noop = () => {};
const noopAsync = async () => {};

const NotificationCenterContext = createContext<NotificationCenterContextType>({
  notifications: [],
  unreadCount: 0,
  dismissNotification: noop,
  dismissAll: noop,
  markNotificationRead: noop,
  markAllRead: noop,
  browserNotifications: {
    enabled: false,
    permission: 'default',
    requestAndEnable: noopAsync,
    disable: noop,
    fire: noop,
  },
  globalNotifEnabled: true,
  toggleGlobalNotifications: noop,
});

declare global {
  interface Window {
    __notificationCenter?: {
      inject: (raw: Record<string, unknown>) => void;
      clearAll: () => void;
    };
  }
}

/** Wire payloads may use snake_case strings or numeric ids; never collapse to '' for non-strings. */
function str(v: unknown): string {
  if (typeof v === 'string') {
    return v;
  }
  if (typeof v === 'number' || typeof v === 'bigint') {
    return String(v);
  }
  if (v === null || v === undefined) {
    return '';
  }
  return String(v);
}
function numOrNull(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}
function notificationDate(timestamp?: string): Date {
  if (!timestamp) {
    return new Date();
  }
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

/** Map the snake_case wire format from the backend to the generated camelCase interface. */
function normalizeJob(raw: Record<string, unknown>): GenerationJobPayload {
  const strOrNull = (v: unknown) => {
    const s = str(v); return s || null;
  };
  return {
    eventType: str(raw.event_type ?? raw.eventType) as GenerationJobPayload['eventType'],
    category: strOrNull(raw.category) as GenerationJobPayload['category'] ?? undefined,
    timestamp: typeof raw.timestamp === 'string' ? raw.timestamp : undefined,
    reservedLink: strOrNull(raw.link ?? raw.reservedLink),
    firmId: numOrNull(raw.firm_id ?? raw.firmId),
    firmName: strOrNull(raw.firm_name ?? raw.firmName),
    firmSlug: strOrNull(raw.firm_slug ?? raw.firmSlug),
    companyId: typeof raw.company_id === 'number' ? raw.company_id : (raw.companyId as number),
    companyName: str(raw.company_name ?? raw.companyName),
    companySlug: strOrNull(raw.company_slug ?? raw.companySlug),
    valuationId: numOrNull(raw.valuation_id ?? raw.valuationId),
    jobId: str(raw.job_id ?? raw.jobId),
    processingType: str(raw.processing_type ?? raw.processingType),
    reservedStatus: str(raw.status ?? raw.reservedStatus),
    errorMessage: typeof raw.error_message === 'string' ? raw.error_message : (raw.errorMessage as string | null | undefined) ?? null,
    initiatedByUserId: numOrNull(raw.initiated_by_user_id ?? raw.initiatedByUserId),
    filenames: Array.isArray(raw.filenames) ? (raw.filenames as unknown[]).filter((f): f is string => typeof f === 'string') : undefined,
  };
}

function mapJobStatusToNotificationStatus(status: string): NotificationStatus {
  switch (status.toUpperCase()) {
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
      return 'failed';
    default:
      return 'in_progress';
  }
}

interface ChannelListenerProps {
  channelName: string;
  companySlug: string | undefined;
  userId: number | undefined;
  /** Stable time when the notification center mounted; used to avoid toast spam from rewind. */
  hydrationCutoffMs: number;
  onMessage: (notification: AppNotification, isNew: boolean, showToast: boolean) => void;
  getExistingNotification: (jobId: string) => AppNotification | undefined;
  onJobCompleted?: (job: GenerationJobPayload) => Promise<void>;
  onJobFailed?: (job: GenerationJobPayload) => void;
}

function isLikelyRewindMessage(message: Message, hydrationCutoffMs: number): boolean {
  const t = message.timestamp;
  if (typeof t !== 'number') {
    return false;
  }
  return t < hydrationCutoffMs - REWIND_TOAST_CUTOFF_MS;
}

const NotificationChannelListener = ({
  channelName,
  companySlug,
  userId,
  hydrationCutoffMs,
  onMessage,
  getExistingNotification,
  onJobCompleted,
  onJobFailed,
}: ChannelListenerProps) => {
  useChannel(channelName, (message: Message) => {
    let raw: Record<string, unknown>;
    try {
      raw = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;
    } catch {
      return;
    }
    const rawJobId = raw.job_id ?? raw.jobId;
    if (rawJobId === undefined || rawJobId === null || rawJobId === '') {
      return;
    }

    const job = normalizeJob(raw);
    const status = mapJobStatusToNotificationStatus(job.reservedStatus);
    const existing = getExistingNotification(job.jobId);
    const eventDate = notificationDate(job.timestamp);
    let serialFromMessage: string | undefined;
    if (typeof message.serial === 'string') {
      serialFromMessage = message.serial;
    } else if (typeof message.serial === 'number' || typeof message.serial === 'bigint') {
      serialFromMessage = String(message.serial);
    } else {
      serialFromMessage = undefined;
    }
    const serial = serialFromMessage ?? existing?.ablySerial;

    const resolvedCompanySlug = job.companySlug ?? companySlug;
    const existingJob = existing?.type === NOTIFICATION_TYPE.JOB ? existing : null;

    let notification: JobAppNotification = existingJob
      ? { ...existingJob, status, job, updatedAt: eventDate,
        ablySerial: serial ?? existingJob.ablySerial,
        firmSlug: job.firmSlug ?? existingJob.firmSlug ?? undefined,
        companySlug: job.companySlug ?? existingJob.companySlug }
      : {
        type: NOTIFICATION_TYPE.JOB,
        id: job.jobId,
        status,
        job,
        addedAt: eventDate,
        updatedAt: eventDate,
        dismissed: false,
        ablySerial: serial,
        firmSlug: job.firmSlug ?? undefined,
        companySlug: resolvedCompanySlug,
      };

    notification = applyAnnotationSummaryFromMessage(message, notification, userId);

    const isNew = !existingJob || (existingJob.status !== status && (status === 'completed' || status === 'failed'));
    const fromRewind = isLikelyRewindMessage(message, hydrationCutoffMs);
    const showToast = isNew && !fromRewind;

    onMessage(notification, isNew, showToast);

    if (status === 'completed' && onJobCompleted && !fromRewind) {
      onJobCompleted(job);
    } else if (status === 'failed' && onJobFailed && !fromRewind) {
      onJobFailed(job);
    }
  });

  return null;
};


function normalizeAnnouncement(
  raw: Record<string, unknown>,
  message: Message
): GlobalAppNotification | GeneralAppNotification | null {
  const categoryRaw = String(raw.category ?? '').toLowerCase();
  const eventTypeRaw = String(raw.event_type ?? '').toLowerCase();
  const type = (categoryRaw === 'general' || eventTypeRaw === 'general')
    ? NOTIFICATION_TYPE.GENERAL
    : NOTIFICATION_TYPE.GLOBAL;
  let serialFromMessage: string | undefined;
  if (typeof message.serial === 'string') {
    serialFromMessage = message.serial;
  } else if (typeof message.serial === 'number') {
    serialFromMessage = String(message.serial);
  } else if (typeof message.id === 'string') {
    serialFromMessage = message.id;
  } else {
    serialFromMessage = undefined;
  }
  if (!serialFromMessage) return null;

  const title = typeof raw.title === 'string' ? raw.title : '';
  if (!title) return null;
  const content = typeof raw.content === 'string' ? raw.content : '';

  const link = typeof raw.link === 'string' && raw.link ? raw.link : undefined;
  const linkLabel = typeof raw.link_label === 'string' && raw.link_label ? raw.link_label : undefined;

  const now = new Date();
  const base = {
    id: serialFromMessage,
    title,
    content,
    link,
    linkLabel,
    addedAt: now,
    updatedAt: now,
    dismissed: false as const,
    ablySerial: serialFromMessage,
  };

  if (type === NOTIFICATION_TYPE.GLOBAL) {
    return {
      ...base,
      type: NOTIFICATION_TYPE.GLOBAL,
      eventType: String(raw.event_type ?? '').trim().toLowerCase().replace(/\s+/g, '_'),
    };
  }
  return { ...base, type: NOTIFICATION_TYPE.GENERAL };
}

interface BroadcastChannelListenerProps {
  hydrationCutoffMs: number;
  userId: number | undefined;
  onMessage: (notification: AppNotification, isNew: boolean, showToast: boolean) => void;
  getExistingById: (id: string) => AppNotification | undefined;
}

const BroadcastChannelListener = ({
  hydrationCutoffMs,
  userId,
  onMessage,
  getExistingById,
}: BroadcastChannelListenerProps) => {
  useChannel(GLOBAL_CHANNEL_NAME, (message: Message) => {
    let parsed: Record<string, unknown>;
    try {
      parsed = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;
    } catch {
      return;
    }

    // Unwrap Zapier/Ably nesting. Three shapes we've seen in the wild:
    // A) {event_type, title, content, ...}          — direct payload
    // B) {name:"...", data:{event_type, title, ...}} — Zapier wrapper (object data)
    // C) {data: '{"name":"...","data":{...}}'        — Ably delivers form-encoded body as string
    let raw: Record<string, unknown>;
    if (typeof parsed.data === 'string') {
      // Shape C — parse the inner string, then extract .data if it wraps again
      let inner: Record<string, unknown>;
      try {
        inner = JSON.parse(parsed.data) as Record<string, unknown>;
      } catch {
        return;
      }
      raw = inner?.data && typeof inner.data === 'object' && !Array.isArray(inner.data) && 'title' in (inner.data as object)
        ? (inner.data as Record<string, unknown>)
        : inner;
    } else {
      // Shape A or B
      raw = parsed?.data && typeof parsed.data === 'object' && !Array.isArray(parsed.data) && 'title' in (parsed.data as object)
        ? (parsed.data as Record<string, unknown>)
        : parsed;
    }
    const notification = normalizeAnnouncement(raw, message);
    if (!notification) return;
    const applied = applyAnnotationSummaryFromMessage(message, notification, userId);
    const existing = getExistingById(applied.id);
    const isNew = !existing;
    const fromRewind = isLikelyRewindMessage(message, hydrationCutoffMs);
    const showToast = isNew && !fromRewind;
    onMessage(applied, isNew, showToast);
  });
  return null;
};


/** Merge read/dismissed flags from in-memory cache (loaded from IndexedDB on mount). */
function mergeFromFlagsCache<T extends { id: string; read?: boolean; dismissed: boolean; acknowledgedAt?: number }>(
  notification: T,
  flags: { read: Set<string>; dismissed: Map<string, number> }
): T {
  const r = flags.read.has(notification.id);
  const acknowledgedAt = flags.dismissed.get(notification.id);
  const d = acknowledgedAt !== undefined;
  if (!r && !d) {
    return notification;
  }
  return {
    ...notification,
    read: notification.read || r,
    dismissed: notification.dismissed || d,
    acknowledgedAt: notification.acknowledgedAt ?? acknowledgedAt,
  };
}

export interface NotificationCenterProviderProps {
  children: React.ReactNode;
  channelName: string;
  companySlug?: string;
  enableGlobalChannel?: boolean;
  onJobCompleted?: (job: GenerationJobPayload) => Promise<void>;
  onJobFailed?: (job: GenerationJobPayload) => void;
}

function buildBrowserNotificationText(notification: AppNotification): { title: string; body: string } {
  if (notification.type === NOTIFICATION_TYPE.GLOBAL || notification.type === NOTIFICATION_TYPE.GENERAL) {
    return { title: notification.title, body: notification.content };
  }
  const { status, job } = notification;
  let title: string;
  if (status === 'completed') {
    title = 'AI Processing Complete';
  } else if (status === 'failed') {
    title = 'AI Processing Failed';
  } else {
    title = 'Processing Update';
  }
  const body = job.companyName || job.processingType;
  return { title, body };
}

export const NotificationCenterProvider = ({
  children,
  channelName,
  companySlug,
  enableGlobalChannel = false,
  onJobCompleted,
  onJobFailed,
}: NotificationCenterProviderProps) => {
  const [{ user }] = useStore();
  const userId = user?.id;
  const ably = useAbly();
  const browserNotifications = useBrowserNotifications();
  const browserNotificationsRef = useRef(browserNotifications);
  useEffect(() => {
    browserNotificationsRef.current = browserNotifications;
  });


  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toasts, setToasts] = useState<AppNotification[]>([]);
  const [hydrationCutoffMs] = useState(() => Date.now());
  const [globalNotifEnabled, setGlobalNotifEnabled] = useState(
    () => localStorage.getItem(GLOBAL_NOTIF_PREF_KEY) !== 'false'
  );
  const globalNotifEnabledRef = useRef(globalNotifEnabled);
  useEffect(() => {
    globalNotifEnabledRef.current = globalNotifEnabled;
  }, [globalNotifEnabled]);

  const toggleGlobalNotifications = useCallback(() => {
    setGlobalNotifEnabled(prev => {
      const next = !prev;
      localStorage.setItem(GLOBAL_NOTIF_PREF_KEY, String(next));
      return next;
    });
  }, []);

  /** Keep latest list for Ably callbacks (rewind can fire many messages before React re-renders). */
  const notificationsRef = useRef<AppNotification[]>([]);

  /** In-memory cache of persisted flags; loaded from IndexedDB on mount. Used for sync merge. */
  const flagsRef = useRef<{ read: Set<string>; dismissed: Map<string, number> }>({
    read: new Set(),
    dismissed: new Map(),
  });

  useEffect(() => {
    if (userId === undefined) {
      return;
    }
    migrateFromLocalStorage(userId)
      .then(() => loadFlagsForUser(userId))
      .then(flags => {
        flagsRef.current = flags;
      })
      .catch(() => {});
    evictExpired().catch(() => {});
  }, [userId]);
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  /** Open BroadcastChannel and apply incoming cross-tab events to local state. */
  useEffect(() => {
    const bc = openNotificationBroadcastChannel();
    broadcastChannelRef.current = bc;
    if (!bc) {
      return undefined;
    }

    const handler = (event: MessageEvent) => {
      const msg = event.data as NotificationBroadcastEvent;
      if (!msg || msg.userId !== userId) {
        return;
      }
      setNotifications(prev => {
        let next: AppNotification[];
        if (msg.type === 'read') {
          next = prev.map(n => (n.id === msg.id ? { ...n, read: true } : n));
        } else if (msg.type === 'dismissed') {
          next = prev.map(n => (n.id === msg.id ? { ...n, dismissed: true, acknowledgedAt: msg.acknowledgedAt } : n));
        } else if (msg.type === 'dismissedMany') {
          const ids = new Set(msg.ids);
          next = prev.map(n => (ids.has(n.id) ? { ...n, dismissed: true, acknowledgedAt: msg.acknowledgedAt } : n));
        } else {
          return prev;
        }
        notificationsRef.current = next;
        return next;
      });
    };

    bc.addEventListener('message', handler);
    return () => {
      bc.removeEventListener('message', handler);
      bc.close();
      broadcastChannelRef.current = null;
    };
  }, [userId]);

  const getExistingNotification = useCallback((id: string) => notificationsRef.current.find(n => n.id === id), []);

  useEffect(() => {
    if (!channelName) {
      return undefined;
    }

    const channel = ably.channels.get(channelName, NOTIFICATION_CHANNEL_OPTIONS);
    const onAnnotation = (annotation: Annotation) => {
      if (annotation.type !== NOTIFICATION_ANNOTATION_DISTINCT_TYPE) {
        return;
      }
      // Only apply annotations published by this user — other users' read/dismiss
      // events arrive on shared channels and must not affect local state.
      if (userId === undefined || annotation.clientId !== String(userId)) {
        return;
      }
      const {name} = annotation;
      if (name !== 'read' && name !== 'deleted') {
        return;
      }
      const {messageSerial} = annotation;
      if (!messageSerial) {
        return;
      }
      setNotifications(prev => {
        let matchedId: string | undefined;
        const next = prev.map(n => {
          if (!n.ablySerial || n.ablySerial !== messageSerial) {
            return n;
          }
          matchedId = n.id;
          if (name === 'deleted') {
            return { ...n, dismissed: true, acknowledgedAt: n.acknowledgedAt ?? Date.now() };
          }
          return { ...n, read: true };
        });
        if (userId !== undefined && matchedId) {
          if (name === 'deleted') {
            const ackTs = Date.now();
            flagsRef.current.dismissed.set(matchedId, ackTs);
            persistDismissed(userId, matchedId).catch(() => {});
          } else {
            flagsRef.current.read.add(matchedId);
            persistRead(userId, matchedId).catch(() => {});
          }
        }
        notificationsRef.current = next;
        return next;
      });
    };

    channel.annotations.subscribe(onAnnotation).catch(() => {});

    return () => {
      channel.annotations.unsubscribe(onAnnotation);
    };
  }, [ably, channelName, userId]);

  const handleMessage = useCallback(
    (notification: AppNotification, isNew: boolean, showToast: boolean) => {
      const merged = mergeFromFlagsCache(notification, flagsRef.current);
      flushSync(() => {
        setNotifications(prev => {
          const rest = prev.filter(n => n.id !== merged.id);
          const next = [merged, ...rest].slice(0, MAX_NOTIFICATION_LIST_SIZE);
          notificationsRef.current = next;
          return next;
        });
      });

      const platformSuppressed =
        (merged.type === NOTIFICATION_TYPE.GLOBAL || merged.type === NOTIFICATION_TYPE.GENERAL) &&
        !globalNotifEnabledRef.current;
      if (isNew && showToast && !platformSuppressed) {
        setToasts(prev => {
          const alreadyInQueue = prev.find(t => t.id === merged.id);
          if (alreadyInQueue) {
            return prev.map(t => (t.id === merged.id ? merged : t));
          }
          return [...prev, merged];
        });
        const { title, body } = buildBrowserNotificationText(merged);
        browserNotificationsRef.current.fire(title, body);
      }
    },
    []
  );

  const dismissNotification = useCallback(
    (id: string) => {
      const target = notificationsRef.current.find(n => n.id === id);
      if (target?.ablySerial && channelName && target.type === NOTIFICATION_TYPE.JOB) {
        try {
          const channel = ably.channels.get(channelName, NOTIFICATION_CHANNEL_OPTIONS);
          publishDistinctAnnotation(channel, target.ablySerial, 'deleted').catch(() => {});
        } catch {} // eslint-disable-line no-empty
      }
      const acknowledgedAt = Date.now();
      // Omit acknowledgedAt from state so the card drops out of the visible filter immediately.
      // acknowledgedAt is still passed to persistence/broadcast for eviction tracking.
      setNotifications(prev => {
        const next = prev.map(n => (n.id === id ? { ...n, dismissed: true, acknowledgedAt: undefined } : n));
        notificationsRef.current = next;
        return next;
      });
      if (userId !== undefined) {
        flagsRef.current.dismissed.set(id, acknowledgedAt);
        persistDismissed(userId, id).catch(() => {});
        broadcastNotificationEvent(broadcastChannelRef.current, { type: 'dismissed', userId, id, acknowledgedAt });
      }
    },
    [ably, channelName, userId]
  );

  const markNotificationRead = useCallback(
    (id: string) => {
      const target = notificationsRef.current.find(n => n.id === id);
      if (!target || target.dismissed || target.read) {
        return;
      }
      if (target.ablySerial && channelName && target.type === NOTIFICATION_TYPE.JOB) {
        try {
          const channel = ably.channels.get(channelName, NOTIFICATION_CHANNEL_OPTIONS);
          publishDistinctAnnotation(channel, target.ablySerial, 'read').catch(() => {});
        } catch {} // eslint-disable-line no-empty
      }
      setNotifications(prev => {
        const next = prev.map(n => (n.id === id ? { ...n, read: true } : n));
        notificationsRef.current = next;
        return next;
      });
      if (userId !== undefined) {
        flagsRef.current.read.add(id);
        persistRead(userId, id).catch(() => {});
        broadcastNotificationEvent(broadcastChannelRef.current, { type: 'read', userId, id });
      }
    },
    [ably, channelName, userId]
  );

  const dismissAll = useCallback(() => {
    const {current} = notificationsRef;
    if (channelName) {
      try {
        const channel = ably.channels.get(channelName, NOTIFICATION_CHANNEL_OPTIONS);
        current.forEach(n => {
          if (!n.dismissed && n.ablySerial && n.type === NOTIFICATION_TYPE.JOB) {
            publishDistinctAnnotation(channel, n.ablySerial, 'deleted').catch(() => {});
          }
        });
      } catch {} // eslint-disable-line no-empty
    }
    const acknowledgedAt = Date.now();
    const idsToStore = current.filter(n => !n.dismissed).map(n => n.id);
    setNotifications(prev => {
      // Omit acknowledgedAt so the visible filter drops them immediately
      // (single-dismiss keeps acknowledgedAt for the 1-hour soft-show window).
      const next = prev.map(n => ({ ...n, dismissed: true, acknowledgedAt: undefined }));
      notificationsRef.current = next;
      return next;
    });
    if (userId !== undefined && idsToStore.length > 0) {
      idsToStore.forEach(id => flagsRef.current.dismissed.set(id, acknowledgedAt));
      persistDismissedMany(userId, idsToStore).catch(() => {});
      broadcastNotificationEvent(broadcastChannelRef.current, { type: 'dismissedMany', userId, ids: idsToStore, acknowledgedAt });
    }
  }, [ably, channelName, userId]);

  const markAllRead = useCallback(() => {
    const unread = notificationsRef.current.filter(n => !n.dismissed && !n.read);
    if (unread.length === 0) {
      return;
    }
    if (channelName) {
      const channel = ably.channels.get(channelName, NOTIFICATION_CHANNEL_OPTIONS);
      unread.forEach(n => {
        if (n.ablySerial && n.type === NOTIFICATION_TYPE.JOB) {
          publishDistinctAnnotation(channel, n.ablySerial, 'read').catch(() => {});
        }
      });
    }
    setNotifications(prev => {
      const next = prev.map(n => (n.dismissed || n.read ? n : { ...n, read: true }));
      notificationsRef.current = next;
      return next;
    });
    if (userId !== undefined) {
      unread.forEach(n => {
        flagsRef.current.read.add(n.id);
        persistRead(userId, n.id).catch(() => {});
        broadcastNotificationEvent(broadcastChannelRef.current, { type: 'read', userId, id: n.id });
      });
    }
  }, [ably, channelName, userId]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      return undefined;
    }
    // eslint-disable-next-line no-underscore-dangle
    window.__notificationCenter = {
      inject: (raw: Record<string, unknown>) => {
        const job = normalizeJob(raw);
        const status = mapJobStatusToNotificationStatus(job.reservedStatus);
        const now = new Date();
        const notification: JobAppNotification = { type: NOTIFICATION_TYPE.JOB, id: job.jobId, status, job, addedAt: now, updatedAt: now, dismissed: false };
        handleMessage(notification, true, false);
      },
      clearAll: () => dismissAll(),
    };
    return () => {
      // eslint-disable-next-line no-underscore-dangle
      delete window.__notificationCenter;
    };
  }, [handleMessage, dismissAll]);

  const unreadCount = notifications.filter(
    n => !n.dismissed && !n.read &&
      !((n.type === NOTIFICATION_TYPE.GLOBAL || n.type === NOTIFICATION_TYPE.GENERAL) && !globalNotifEnabled)
  ).length;

  return (
    <NotificationCenterContext.Provider
      value={{ notifications, unreadCount, dismissNotification, dismissAll, markNotificationRead, markAllRead, browserNotifications, globalNotifEnabled, toggleGlobalNotifications }}>
      {channelName && (
        <ChannelProvider channelName={channelName} options={NOTIFICATION_CHANNEL_OPTIONS}>
          <NotificationChannelListener
            channelName={channelName}
            companySlug={companySlug}
            userId={userId}
            hydrationCutoffMs={hydrationCutoffMs}
            onMessage={handleMessage}
            getExistingNotification={getExistingNotification}
            onJobCompleted={onJobCompleted}
            onJobFailed={onJobFailed}
          />
        </ChannelProvider>
      )}
      {enableGlobalChannel && (
        <ChannelProvider channelName={GLOBAL_CHANNEL_NAME} options={GLOBAL_CHANNEL_OPTIONS}>
          <BroadcastChannelListener
            hydrationCutoffMs={hydrationCutoffMs}
            userId={userId}
            onMessage={handleMessage}
            getExistingById={getExistingNotification}
          />
        </ChannelProvider>
      )}
      {children}
      <NotificationToastStack toasts={toasts} onDismiss={dismissToast} />
    </NotificationCenterContext.Provider>
  );
};

export const useNotificationCenter = () => useContext(NotificationCenterContext);
